import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BigNumber from 'bignumber.js'
import { useAtom, useAtomValue } from 'jotai/react'

import {
  FeeMode,
  ITxSettingProps,
  PRIORITY_FEE_LEVEL,
  SUPPORT_FEE_MODE
} from '@/constants/settings'
import { Decimal } from '@/constants/trade'
import useMediaQuery from '@/hooks/useMediaQuery'
import { fetchQuiknodePriorityFees } from '@/lib/apiFetcher'
import { filterPositive } from '@/lib/format'
import { min2Max } from '@/lib/utils'
import { solPriceUsdAtom } from '@/stores/services'
import { transactionSettingAtom } from '@/stores/settings'

import { CardAlert } from '../common/AlertDialog'
import { Flex, Grid } from '../common/Box'
import { BeautyButton, Button, ButtonRadioGroup } from '../common/Button'
import DropdownMenu from '../common/DropdownMenu'
import { NumberInput } from '../common/Input'
import { Dividing } from '../common/Structure'
import { OrbitronText, Text } from '../common/Text'
import { Close, Fee } from '../svgr/common'

import { Description, SubTitle } from './Title'

type TFormData = Omit<ITxSettingProps, 'slippage' | 'mevState'>

const GlobalPriorityFee: React.FC<{
  className?: string
  wrapperClassName?: string
}> = ({ className, wrapperClassName }) => {
  const { gtXL } = useMediaQuery()
  const { t } = useTranslation()

  const solPriceUsd = useAtomValue(solPriceUsdAtom)

  const [priorityFeeSetting, setPriorityFeeSetting] = useAtom(
    transactionSettingAtom
  )

  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState<TFormData>({} as TFormData)
  const [showFeeAlert, setShowFeeAlert] = useState<boolean>(false)
  const [extremeFee, setExtremeFee] = useState('')

  const feeModeSelected = useMemo(
    () => SUPPORT_FEE_MODE.find(({ value }) => value === formData.feeMode),
    [formData.feeMode]
  )

  const memoFeeValueUsd = useMemo(() => {
    return new BigNumber(formData.feeValue || 0)
      .multipliedBy(solPriceUsd || 0)
      .toFixed(2)
  }, [formData.feeValue, solPriceUsd])

  // const handleFee = useCallback(
  //   (ev: React.ChangeEvent<HTMLInputElement>) => {
  //     const value = ev.target.value
  //     setFee(value)
  //   },
  //   [setFee]
  // )

  const handleDropdownMenu = useCallback(() => {
    setOpen((prev) => !prev)
  }, [setOpen])

  const onChangeFormData = (key: string, value: string) => {
    const obj = { [key]: value }
    setFormData((prev) => ({
      ...prev,
      ...obj
    }))
  }

  const onChangeFeeInput = (event: any) => {
    const val = filterPositive(event.target.value, Decimal.SOL)
    onChangeFormData('feeValue', val)
  }

  const onActionAlert = (action: boolean) => {
    if (action) {
      handleSaveChange()
    }
  }

  const onClickSave = () => {
    if (
      isChange &&
      formData.feeMode === FeeMode.ExactFee &&
      BigNumber(extremeFee).multipliedBy(10).lt(formData.feeValue)
    ) {
      // 设置的优先费大于当前优先费的10倍时二次确认
      setShowFeeAlert(true)
    } else {
      handleSaveChange()
    }
  }

  const handleSaveChange = () => {
    const original = (priorityFeeSetting || {}) as any
    const flag = Object.keys(formData).some((key: string) => {
      return (formData as any)[key] !== original[key]
    })
    if (flag) {
      setPriorityFeeSetting({
        ...priorityFeeSetting,
        ...formData,
        feeValue: formData.feeValue || '0'
      })
    }
    setOpen(false)
  }

  const isChange = useMemo(() => {
    const original = (priorityFeeSetting || {}) as any
    const flag = Object.keys(formData).some((key: string) => {
      return (formData as any)[key] !== original[key]
    })
    return flag
  }, [formData, priorityFeeSetting])

  const getRealTimePriorityFees = useCallback(async () => {
    const resp: any = await fetchQuiknodePriorityFees().catch(() => undefined)
    const extremeValue = min2Max(resp?.per_compute_unit?.extreme, Decimal.SOL)
    setExtremeFee(extremeValue)
  }, [])

  useLayoutEffect(() => {
    if (open) {
      setFormData({
        txBroadcast: priorityFeeSetting?.txBroadcast || '',
        feeLevel: priorityFeeSetting?.feeLevel || '',
        feeMode: priorityFeeSetting?.feeMode || '',
        feeValue: priorityFeeSetting?.feeValue || ''
      })
      getRealTimePriorityFees()
    }
  }, [open])

  return (
    <>
      <DropdownMenu
        open={open}
        onOpenChange={setOpen}
        content={{
          className: 'max-w-[560px] xl:w-[480px] relative',
          onInteractOutside: (event) => event.preventDefault()
        }}
        trigger={{
          asChild: true,
          children: (
            <BeautyButton
              beauty={false}
              selector={gtXL}
              open={open}
              wrapperClassName={wrapperClassName}
              className={className}
              onClick={handleDropdownMenu}
            >
              {gtXL ? (
                <Text className="text-sm font-semibold leading-none xl:text-base">
                  {priorityFeeSetting?.txBroadcast}
                </Text>
              ) : (
                <Fee />
              )}
            </BeautyButton>
          )
        }}
      >
        <button
          type="button"
          className="absolute right-2 top-2 flex size-8 items-center justify-center xl:right-4 xl:top-4"
          onClick={() => setOpen(false)}
        >
          <Close />
        </button>
        <Grid className="gap-4 pt-8 xl:gap-6">
          {/* <Grid className="gap-2 xl:gap-3">
            <Title>{t('settings.priority.globalPriorityFee')}</Title>
            <Description>
              {t('settings.priority.globalPriorityFee.description')}
            </Description>
          </Grid> */}
          {/* <Grid className="gap-2 xl:gap-3">
            <SubTitle>{t('settings.priority.transactionBroadcasting')}</SubTitle>
            <ButtonRadioGroup
              options={SUPPORT_TRANSACTION_BROADCASTING}
              value={formData.txBroadcast}
              onValueChange={(value) => onChangeFormData('txBroadcast', value)}
            />
            <Description>
              {t('settings.priority.transactionBroadcasting.description')}
            </Description>
          </Grid>
          <Dividing /> */}
          {formData.feeMode === FeeMode.MaxCap && (
            <>
              <Grid className="gap-2 xl:gap-3">
                <SubTitle>{t('settings.priority.priorityLevel')}</SubTitle>
                <ButtonRadioGroup
                  options={PRIORITY_FEE_LEVEL}
                  value={formData.feeLevel}
                  onValueChange={(val) => onChangeFormData('feeLevel', val)}
                />
                {/* <Description>
                  {t('settings.priority.transactionBroadcasting.description')}
                </Description> */}
              </Grid>
              <Dividing />
            </>
          )}

          <Grid className="gap-2 xl:gap-3">
            <Flex className="items-center justify-between">
              <SubTitle>{t('settings.priority.feeMode')}</SubTitle>
              <ButtonRadioGroup
                variant="secondary"
                options={SUPPORT_FEE_MODE}
                value={formData.feeMode}
                onValueChange={(value) => onChangeFormData('feeMode', value)}
              />
            </Flex>
            <Description>
              {formData.feeMode === FeeMode.MaxCap
                ? t('settings.priority.maxCapDesc')
                : t('settings.priority.exactFeeDesc')}
            </Description>
          </Grid>
          <Grid className="gap-2 xl:gap-3">
            <Flex className="items-center justify-between">
              <SubTitle>{feeModeSelected?.label}</SubTitle>
              <Text variant="tertiary" className="text-xs xl:text-base">
                ≈${memoFeeValueUsd}
              </Text>
            </Flex>
            <NumberInput
              size="md"
              className="font-Orbitron"
              placeholder="0"
              value={formData.feeValue}
              onChange={onChangeFeeInput}
              suffixNode={
                <OrbitronText className="text-xs xl:text-lg">SOL</OrbitronText>
              }
            />
            {/* {fee && (
              <Description>
                {t('settings.priority.referrenceTip')} {fee} SOL
              </Description>
            )} */}
          </Grid>
          <Button
            border={false}
            size="sm"
            className="font-Orbitron"
            onClick={onClickSave}
          >
            {isChange ? t('settings.priority.saveChanges') : t('common.close')}
          </Button>
        </Grid>
      </DropdownMenu>
      <CardAlert
        open={showFeeAlert}
        onOpenChange={setShowFeeAlert}
        showCancel={true}
        onAction={onActionAlert}
      >
        <Text className="px-2 py-2">
          {t('setting.message.priorityFeeConfirm', { var1: extremeFee })}
        </Text>
      </CardAlert>
    </>
  )
}

export default GlobalPriorityFee
